import type { tCloseSettings } from './LeadClose.types';

/** The server's limits (leads-service LeadCloseSettingsEditor). */
export const MAX_OPTION_LABEL = 40;
export const MAX_LIVE_OPTIONS = 20;

export type tOptionList = 'lost' | 'resolved';
export type tDraftRow = { uuid: string | null; label: string };
export type tOptionsDraft = {
  lost: tDraftRow[];
  resolved: tDraftRow[];
  allowOther: Record<tOptionList, boolean>;
};
export type tOptionOp =
  | { op: 'add'; label: string }
  | { op: 'rename'; uuid: string; label: string }
  | { op: 'remove'; uuid: string };
export type tDraftError = {
  key: 'label_too_long' | 'label_taken' | 'list_empty' | 'list_full';
  type: tOptionList;
  index: number | null;
};

const LISTS: tOptionList[] = ['lost', 'resolved'];

const clean = (label: string) => label.replace(/\s+/g, ' ').trim();

/** LabelKey::of: no accents, no case, one space. */
export function labelKey(label: string): string {
  return clean(label)
    .normalize('NFD')
    .replace(/\p{Mn}+/gu, '')
    .toLowerCase();
}

export function draftFromSettings(settings: tCloseSettings): tOptionsDraft {
  return {
    lost: settings.lost.map(({ uuid, label }) => ({ uuid, label })),
    resolved: settings.resolved.map(({ uuid, label }) => ({ uuid, label })),
    allowOther: {
      lost: settings.allow_other_lost,
      resolved: settings.allow_other_resolved,
    },
  };
}

/** The first thing the server would refuse, said where it is (the prototype's applyOptions). */
export function draftError(draft: tOptionsDraft): tDraftError | null {
  for (const type of LISTS) {
    const seen = new Set<string>();
    let live = 0;
    for (const [index, row] of draft[type].entries()) {
      const label = clean(row.label);
      if (!label) continue;
      if (label.length > MAX_OPTION_LABEL)
        return { key: 'label_too_long', type, index };
      const key = labelKey(label);
      if (seen.has(key)) return { key: 'label_taken', type, index };
      seen.add(key);
      live += 1;
    }
    if (live > MAX_LIVE_OPTIONS) return { key: 'list_full', type, index: null };
    if (live === 0 && !draft.allowOther[type])
      return { key: 'list_empty', type, index: null };
  }
  return null;
}

/**
 * The draft as explicit operations against the settings it was read from:
 * one per added, renamed or removed option, and the switches that changed.
 * Null when nothing changed (no request).
 */
export function settingsPatch(
  settings: tCloseSettings,
  draft: tOptionsDraft,
): Record<string, unknown> | null {
  const body: Record<string, unknown> = {};
  for (const type of LISTS) {
    const ops: tOptionOp[] = [];
    const kept = new Set<string>();
    const before = new Map(settings[type].map((o) => [o.uuid, o.label]));
    const adds: tOptionOp[] = [];
    for (const row of draft[type]) {
      const label = clean(row.label);
      if (row.uuid && before.has(row.uuid)) {
        if (!label) continue; // emptied: removed below
        kept.add(row.uuid);
        if (label !== before.get(row.uuid))
          ops.push({ op: 'rename', uuid: row.uuid, label });
      } else if (label) adds.push({ op: 'add', label });
    }
    for (const option of settings[type])
      if (!kept.has(option.uuid)) ops.push({ op: 'remove', uuid: option.uuid });
    // Removed before added: a name freed by a removal can be added back.
    const all = [...ops, ...adds];
    if (all.length) body[type] = all;
    const flag = `allow_other_${type}` as const;
    if (draft.allowOther[type] !== settings[flag])
      body[flag] = draft.allowOther[type];
  }
  return Object.keys(body).length
    ? { version: settings.version, ...body }
    : null;
}
